import { useCallback, useEffect, useRef, useState } from "react";
import { SettingsApiDataSource } from "../../data/datasources/SettingsApiDataSource";
import type { LocationSettingsDTO } from "../../data/models/CompanySettingsDTO";

export interface UseLocationsPanelReturn {
  data: LocationSettingsDTO | null;
  loading: boolean;
  /** Page-level failure: nothing could be loaded at all. */
  error: string | null;
  refreshing: boolean;
  /** A failed Refresh, shown beside the button with the old list still on screen. */
  refreshError: string | null;
  /** One line about what the last Refresh did. Cleared when the next one starts. */
  refreshNote: string | null;
  refresh: () => Promise<void>;
  reload: () => Promise<void>;
  /** ZOUR-92 — the id whose GST date is being written, or null. */
  savingGstDateFor: number | null;
  /** A failed save, shown on the row that failed with the table still on screen. */
  gstDateError: string | null;
  /** One line confirming the last successful save. */
  gstDateNote: string | null;
  saveGstDate: (id: number, gstEnabledFrom: string | null) => Promise<void>;
}

/**
 * ZOUR-65 phase 1 — the Locations panel's state.
 *
 * Modelled on `useGstPanel`, minus everything about a form: this panel has no
 * editable fields, because Zoho owns the list and Zournal has no way to write
 * it back (its `POST locations` needs a `tax_settings_id` only Zoho can mint).
 *
 * A FAILED REFRESH DOES NOT CLEAR THE LIST. The rows on screen are the last
 * thing Zoho confirmed, and they are still the truth as far as anyone knows;
 * replacing them with an error would lose real information because a token
 * expired. The error goes beside the button and the table stays.
 */
export const useLocationsPanel = (): UseLocationsPanelReturn => {
  const [data, setData] = useState<LocationSettingsDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [refreshNote, setRefreshNote] = useState<string | null>(null);

  const [savingGstDateFor, setSavingGstDateFor] = useState<number | null>(null);
  const [gstDateError, setGstDateError] = useState<string | null>(null);
  const [gstDateNote, setGstDateNote] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);

  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    []
  );

  const reload = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const payload = await new SettingsApiDataSource().fetchLocations(controller.signal);
      if (controller.signal.aborted) return;
      setData(payload);
    } catch (e: unknown) {
      if (controller.signal.aborted) return;
      setError(
        e instanceof Error ? e.message : "We couldn't load your GST registrations."
      );
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    setRefreshError(null);
    setRefreshNote(null);

    try {
      const payload = await new SettingsApiDataSource().syncLocations();
      setData(payload);
      setRefreshNote(describeSync(payload));
    } catch (e: unknown) {
      setRefreshError(
        e instanceof Error
          ? e.message
          : "We couldn't read your locations from Zoho Books."
      );
    } finally {
      setRefreshing(false);
    }
  }, []);

  /**
   * ZOUR-92 — write one registration's GST date.
   *
   * The same shape as `refresh`: a failure never clears the table, because the
   * rows on screen are still the truth and losing them over a failed save would
   * be a worse outcome than the save not happening.
   */
  const saveGstDate = useCallback(
    async (id: number, gstEnabledFrom: string | null) => {
      setSavingGstDateFor(id);
      setGstDateError(null);
      setGstDateNote(null);

      try {
        const payload = await new SettingsApiDataSource().saveLocationGstDate(
          id,
          gstEnabledFrom
        );
        setData(payload);
        setGstDateNote(
          payload.saved?.gst_enabled_from
            ? `Saved. GST applies from ${payload.saved.gst_enabled_from} for this registration.`
            : "Cleared. This registration now uses the company GST date."
        );
      } catch (e: unknown) {
        setGstDateError(
          e instanceof Error ? e.message : "We couldn't save that date."
        );
      } finally {
        setSavingGstDateFor(null);
      }
    },
    []
  );

  return {
    data,
    loading,
    error,
    refreshing,
    refreshError,
    refreshNote,
    refresh,
    reload,
    savingGstDateFor,
    gstDateError,
    gstDateNote,
    saveGstDate,
  };
};

/**
 * What the Refresh actually did, in one sentence.
 *
 * "Nothing changed" is worth saying out loud: a Refresh that silently redraws
 * the same table leaves the user unsure whether it ran.
 */
const describeSync = (payload: LocationSettingsDTO): string => {
  const sync = payload.sync;

  if (!sync) {
    return "Read from Zoho Books.";
  }

  const parts: string[] = [];

  if (sync.new_added > 0) {
    parts.push(`${sync.new_added} added`);
  }
  if (sync.deactivated > 0) {
    parts.push(`${sync.deactivated} no longer in Zoho Books, marked inactive`);
  }

  if (parts.length === 0) {
    return `Read from Zoho Books. ${sync.total_from_zoho} location${
      sync.total_from_zoho === 1 ? "" : "s"
    }, nothing changed.`;
  }

  return `Read from Zoho Books. ${parts.join(" · ")}.`;
};
